import { type ActionItem, type DailyCallStats } from "@repo/common";
import {
	adminActionsCollection,
	allAgentsQuery,
	allServicesQuery,
	allUsersQuery,
	auditLogsCollection,
	companiesCollection,
	db,
	phoneNumbersCollection,
	subscriptionsCollection,
	testRoutingCollection,
	whatsappSendersCollection,
} from "@repo/ui";
import {
	collectionGroup,
	doc,
	getDoc,
	getDocs,
	limit,
	onSnapshot,
	orderBy,
	type QueryDocumentSnapshot,
	query,
	startAfter,
	where,
} from "firebase/firestore";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { type Call, toCallUi } from "../data/calls";
import type { LiveKitEvent } from "../data/liveKitEvents";
import {
	compareLiveKitEventRecords,
	liveKitEventsCollection,
	toLiveKitUiEvent,
} from "../data/liveKitEvents";
import type {
	AdminAction,
	Agent,
	AuditLog,
	Company,
	PhoneNumber,
	Service,
	Subscription,
	TestRouting,
	User,
	WhatsappSender,
} from "../types";

export type AdminDailyCallStats = DailyCallStats & { companyId: string };

import {
	compareTcdrRecords,
	parseProcessedTranscripts,
	parseRecordingManifest,
	parseRecordingTranscripts,
	type TCDR,
	type TcdrRecord,
	tcdrsCollectionGroup,
	toTcdrUi,
} from "../data/tcdrs";
import {
	compareTwilioErrorRecords,
	type TwilioError,
	toTwilioUiError,
	twilioErrorsCollection,
} from "../data/twilioErrors";
import {
	compareTwilioEventRecords,
	type TwilioEvent,
	toTwilioUiEvent,
	twilioEventsCollection,
} from "../data/twilioEvents";

interface DataContextType {
	// Events data
	twilioErrors: TwilioError[];
	twilioEvents: TwilioEvent[];
	liveKitEvents: LiveKitEvent[];

	// T-CDR data
	tcdrs: TCDR[];
	isLoadingMoreTcdrs: boolean;
	hasMoreTcdrs: boolean;
	loadMoreTcdrs: () => Promise<void>;

	// Calls data
	calls: Call[];

	// Company, User, and Service data
	companies: Company[];
	users: User[];
	services: Service[];

	// Phone Number and Agent data
	phoneNumbers: PhoneNumber[];
	whatsappSenders: WhatsappSender[];
	agents: Agent[];
	testRouting: TestRouting[];

	// Audit Logs
	auditLogs: AuditLog[];

	// Subscriptions & adminActions
	subscriptions: Subscription[];
	adminActions: AdminAction[];

	// Aggregated call stats
	dailyCallStats: AdminDailyCallStats[];
}

const DataContext = createContext<DataContextType | undefined>(undefined);
const TCDR_PAGE_SIZE = 50;

async function loadTcdrResources(records: TcdrRecord[]) {
	const resourcePaths = Array.from(
		new Set(
			records
				.map((record) => record.resourcesPath)
				.filter((path): path is string => Boolean(path)),
		),
	);

	const results = new Map<
		string,
		{
			recordingManifest?: { recordingUrl?: string };
			recordingTranscripts?: ReturnType<typeof parseRecordingTranscripts>;
			processedTranscripts?: {
				formattedTranscript?: string;
				summary?: string;
				title?: string;
			};
		}
	>();

	await Promise.all(
		resourcePaths.map(async (path) => {
			try {
				const [manifestSnap, recordingSnap, processedSnap] = await Promise.all([
					getDoc(doc(db, path, "recordingManifest")),
					getDoc(doc(db, path, "recordingTranscripts")),
					getDoc(doc(db, path, "processedTranscripts")),
				]);

				const recordingManifest = manifestSnap.exists()
					? parseRecordingManifest(manifestSnap.data())
					: undefined;
				const recordingTranscripts = recordingSnap.exists()
					? parseRecordingTranscripts(recordingSnap.data())
					: undefined;
				const processedTranscripts = processedSnap.exists()
					? parseProcessedTranscripts(processedSnap.data())
					: undefined;

				results.set(path, {
					recordingManifest,
					recordingTranscripts,
					processedTranscripts,
				});
			} catch (error) {
				console.error(`Failed to load resources at ${path}`, error);
			}
		}),
	);

	return results;
}

async function hydrateTcdrRecords(records: TcdrRecord[]): Promise<TCDR[]> {
	const resourcesMap = await loadTcdrResources(records);

	return records.map((record) => {
		const resources = record.resourcesPath
			? resourcesMap.get(record.resourcesPath)
			: undefined;

		return toTcdrUi(
			record,
			resources?.recordingManifest,
			resources?.recordingTranscripts,
			resources?.processedTranscripts,
		);
	});
}

function tcdrKey(tcdr: TCDR): string {
	return `${tcdr.callPath ?? ""}:${tcdr.sid}`;
}

export function DataProvider({
	children,
	currentPage,
}: {
	children: ReactNode;
	currentPage: string;
}) {
	const [twilioErrors, setTwilioErrors] = useState<TwilioError[]>([]);
	const [twilioEvents, setTwilioEvents] = useState<TwilioEvent[]>([]);
	const [liveKitEvents, setLiveKitEvents] = useState<LiveKitEvent[]>([]);
	const [recentTcdrs, setRecentTcdrs] = useState<TCDR[]>([]);
	const [olderTcdrs, setOlderTcdrs] = useState<TCDR[]>([]);
	const [tcdrCursor, setTcdrCursor] =
		useState<QueryDocumentSnapshot<TcdrRecord> | null>(null);
	const [hasMoreTcdrs, setHasMoreTcdrs] = useState(false);
	const [isLoadingMoreTcdrs, setIsLoadingMoreTcdrs] = useState(false);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [users, setUsers] = useState<User[]>([]);
	const [services, setServices] = useState<Service[]>([]);
	const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
	const [whatsappSenders, setWhatsappSenders] = useState<WhatsappSender[]>([]);
	const [agents, setAgents] = useState<Agent[]>([]);
	const [testRouting, setTestRouting] = useState<TestRouting[]>([]);
	const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
	const [calls, setCalls] = useState<Call[]>([]);
	const [actionItems, setActionItems] = useState<ActionItem[]>([]);
	const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
	const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
	const [dailyCallStats, setDailyCallStats] = useState<AdminDailyCallStats[]>(
		[],
	);
	const tcdrs = useMemo(() => {
		const seen = new Set<string>();
		return [...recentTcdrs, ...olderTcdrs]
			.filter((tcdr) => {
				const key = tcdrKey(tcdr);
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			})
			.sort((a, b) => {
				const aTime = a.dateCreated ? Date.parse(a.dateCreated) : 0;
				const bTime = b.dateCreated ? Date.parse(b.dateCreated) : 0;
				return bTime - aTime;
			});
	}, [olderTcdrs, recentTcdrs]);
	const shouldLoadCompanies = [
		"dashboard",
		"companies",
		"company-details",
		"users",
		"user-details",
		"services",
		"service-details",
		"phone-numbers",
		"whatsapp-senders",
		"test-routing",
		"agents",
		"agent-details",
		"calls",
		"cogs",
		"callers",
		"admin-tasks",
		"developer-tests",
		"playbook-runs",
	].includes(currentPage);
	const shouldLoadUsers = ["users", "user-details", "company-details"].includes(
		currentPage,
	);
	const shouldLoadServices = [
		"services",
		"service-details",
		"company-details",
		"agents",
		"agent-details",
	].includes(currentPage);
	const shouldLoadPhoneNumbers = [
		"phone-numbers",
		"agents",
		"agent-details",
	].includes(currentPage);
	const shouldLoadWhatsappSenders = [
		"whatsapp-senders",
		"developer-tests",
	].includes(currentPage);
	const shouldLoadAgents = [
		"dashboard",
		"phone-numbers",
		"whatsapp-senders",
		"test-routing",
		"agents",
		"agent-details",
		"users",
		"playbook-runs",
	].includes(currentPage);
	const shouldLoadTestRouting = ["test-routing", "users"].includes(currentPage);
	const shouldLoadEvents = currentPage === "events";
	const shouldLoadTcdrs =
		currentPage === "tcdrs" || currentPage === "agent-details";
	const shouldLoadCalls = currentPage === "calls";
	const shouldLoadAuditLogs = currentPage === "audit-logs";
	const shouldLoadSubscriptions = [
		"dashboard",
		"companies",
		"company-details",
		"admin-tasks",
	].includes(currentPage);
	const shouldLoadAdminActions = [
		"dashboard",
		"company-details",
		"admin-tasks",
	].includes(currentPage);
	const shouldLoadDailyStats = [
		"dashboard",
		"company-details",
		"agent-details",
	].includes(currentPage);

	useEffect(() => {
		if (!shouldLoadCalls) {
			setCalls([]);
			return;
		}

		console.log("Admin DataContext: Initializing calls listener...");
		try {
			const q = query(
				collectionGroup(db, "calls"),
				orderBy("createdAt", "desc"),
			);
			const unsubscribe = onSnapshot(
				q,
				(snapshot) => {
					console.log(
						`Admin DataContext: Received ${snapshot.docs.length} calls.`,
					);
					const newCalls = snapshot.docs
						.map((doc) => {
							const path = doc.ref.path;
							if (!path.startsWith("companies/")) {
								console.warn(
									"Found call in unexpected path:",
									path,
									doc.data(),
								);
							}
							return { doc, path };
						})
						// Filter out calls that are not in the companies collection
						.filter(
							({ path }) =>
								path.split("/").length === 4 && path.startsWith("companies/"),
						)
						.map(({ doc }) => {
							// Assuming structure: /companies/{companyId}/calls/{callId}
							const companyId = doc.ref.parent.parent?.id || "unknown";
							return toCallUi(doc.id, doc.data(), companyId);
						})
						// TODO(Backfill): Remove this in-memory sort and revert the query to `orderBy("occurredAt", "desc")`
						// once the backfill script for `occurredAt` has been fully applied to production.
						.sort((a, b) => {
							const aTime =
								a.occurredAt?.getTime() ?? new Date(a.date).getTime();
							const bTime =
								b.occurredAt?.getTime() ?? new Date(b.date).getTime();
							return bTime - aTime;
						});
					setCalls(newCalls);
				},
				(err) => {
					console.error(
						"Admin DataContext: Failed to load calls from Firestore",
						err,
					);
				},
			);
			return () => {
				console.log("Admin DataContext: Unsubscribing from calls listener.");
				unsubscribe();
			};
		} catch (error) {
			console.error(
				"Admin DataContext: Error setting up calls listener",
				error,
			);
		}
	}, [shouldLoadCalls]);

	useEffect(() => {
		if (!shouldLoadCalls) {
			setActionItems([]);
			return;
		}

		const unsubscribe = onSnapshot(
			query(
				collectionGroup(db, "actionItems"),
				orderBy("createdAt", "desc"),
				limit(200),
			),
			(snapshot) => {
				const items = snapshot.docs.map((doc) => doc.data() as ActionItem);
				setActionItems(items);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadCalls]);

	useEffect(() => {
		if (!shouldLoadTestRouting) {
			setTestRouting([]);
			return;
		}

		const unsubscribe = onSnapshot(testRoutingCollection(db), (snapshot) => {
			setTestRouting(
				snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
			);
		});
		return unsubscribe;
	}, [shouldLoadTestRouting]);

	useEffect(() => {
		if (!shouldLoadUsers) {
			setUsers([]);
			return;
		}

		const unsubscribe = onSnapshot(
			allUsersQuery(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setUsers(records);
			},
			(error) => {
				console.error("Failed to load Users from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadUsers]);

	useEffect(() => {
		if (!shouldLoadCompanies) {
			setCompanies([]);
			return;
		}

		const unsubscribe = onSnapshot(
			companiesCollection(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setCompanies(records);
			},
			(error) => {
				console.error("Failed to load Companies from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadCompanies]);

	useEffect(() => {
		if (!shouldLoadServices) {
			setServices([]);
			return;
		}

		const unsubscribe = onSnapshot(
			allServicesQuery(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setServices(records);
			},
			(error) => {
				console.error("Failed to load Services from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadServices]);

	useEffect(() => {
		if (!shouldLoadPhoneNumbers) {
			setPhoneNumbers([]);
			return;
		}

		const unsubscribe = onSnapshot(
			phoneNumbersCollection(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setPhoneNumbers(records);
			},
			(error) => {
				console.error("Failed to load Phone Numbers from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadPhoneNumbers]);

	useEffect(() => {
		if (!shouldLoadWhatsappSenders) {
			setWhatsappSenders([]);
			return;
		}

		const unsubscribe = onSnapshot(
			whatsappSendersCollection(db),
			(snapshot) => {
				setWhatsappSenders(
					snapshot.docs.map((document) => ({
						...document.data(),
						id: document.id,
					})),
				);
			},
			(error) => {
				console.error("Failed to load WhatsApp senders from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadWhatsappSenders]);

	useEffect(() => {
		if (!shouldLoadAuditLogs) {
			setAuditLogs([]);
			return;
		}

		const auditLogsQuery = query(
			auditLogsCollection(db),
			orderBy("timestamp", "desc"),
			limit(500),
		);
		const unsubscribe = onSnapshot(
			auditLogsQuery,
			(snapshot) => {
				setAuditLogs(
					snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
				);
			},
			(error) => {
				console.error("Failed to load Audit Logs from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadAuditLogs]);

	useEffect(() => {
		if (!shouldLoadAgents) {
			setAgents([]);
			return;
		}

		const unsubscribe = onSnapshot(
			allAgentsQuery(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setAgents(records);
			},
			(error) => {
				console.error("Failed to load Agents from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadAgents]);

	useEffect(() => {
		if (!shouldLoadSubscriptions) {
			setSubscriptions([]);
			return;
		}

		const unsubscribe = onSnapshot(
			subscriptionsCollection(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setSubscriptions(records);
			},
			(error) => {
				console.error("Failed to load Subscriptions from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadSubscriptions]);

	useEffect(() => {
		if (!shouldLoadAdminActions) {
			setAdminActions([]);
			return;
		}

		const unsubscribe = onSnapshot(
			adminActionsCollection(db),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				setAdminActions(records);
			},
			(error) => {
				console.error("Failed to load AdminActions from Firestore", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadAdminActions]);

	useEffect(() => {
		if (!shouldLoadDailyStats) {
			setDailyCallStats([]);
			return;
		}

		console.log("Admin DataContext: Initializing dailyCallStats listener...");

		const cutoffDate = new Date();
		cutoffDate.setDate(cutoffDate.getDate() - 30);
		const cutoffKey = [
			cutoffDate.getFullYear(),
			String(cutoffDate.getMonth() + 1).padStart(2, "0"),
			String(cutoffDate.getDate()).padStart(2, "0"),
		].join("-");

		const unsubscribe = onSnapshot(
			query(
				collectionGroup(db, "callStatsDaily"),
				where("dateKey", ">=", cutoffKey),
				orderBy("dateKey", "desc"),
			),
			(snapshot) => {
				console.log(
					`Admin DataContext: Received ${snapshot.docs.length} daily call stats docs.`,
				);
				const stats = snapshot.docs.map((doc) => {
					// Path format: companies/{companyId}/agents/{agentId}/callStatsDaily/{dateKey}
					const companyId =
						doc.ref.parent.parent?.parent?.parent?.id || "unknown";
					return {
						...(doc.data() as DailyCallStats),
						companyId,
					};
				});
				setDailyCallStats(stats);
			},
			(error) => {
				console.error(
					"Admin DataContext: Failed to load dailyCallStats",
					error,
				);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadDailyStats]);

	useEffect(() => {
		if (!shouldLoadEvents) {
			setTwilioErrors([]);
			return;
		}

		console.log("Admin DataContext: Initializing twilioErrors listener...");
		const unsubscribe = onSnapshot(
			query(
				twilioErrorsCollection(db),
				orderBy("Timestamp", "desc"),
				limit(200),
			),
			(snapshot) => {
				console.log(
					`Admin DataContext: Received ${snapshot.docs.length} Twilio errors.`,
				);
				const records = snapshot.docs
					.map((doc) => ({ ...doc.data(), id: doc.id }))
					.filter(Boolean)
					.sort(compareTwilioErrorRecords)
					.map((record) => {
						try {
							return toTwilioUiError(record);
						} catch (err) {
							console.error("Failed to map Twilio error", err);
							return null;
						}
					})
					.filter((r): r is TwilioError => r !== null);
				setTwilioErrors(records);
			},
			(error) => {
				console.error("Admin DataContext: Failed to load Twilio errors", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadEvents]);

	useEffect(() => {
		if (!shouldLoadEvents) {
			setTwilioEvents([]);
			return;
		}

		console.log("Admin DataContext: Initializing twilioEvents listener...");
		const unsubscribe = onSnapshot(
			query(
				twilioEventsCollection(db),
				orderBy("timestamp", "desc"),
				limit(200),
			),
			(snapshot) => {
				console.log(
					`Admin DataContext: Received ${snapshot.docs.length} Twilio events.`,
				);
				const records = snapshot.docs
					.map((doc) => ({ ...doc.data(), id: doc.id }))
					.filter(Boolean)
					.sort(compareTwilioEventRecords)
					.map((record) => {
						try {
							return toTwilioUiEvent(record);
						} catch (err) {
							console.error("Failed to map Twilio event", err);
							return null;
						}
					})
					.filter((r): r is TwilioEvent => r !== null);
				setTwilioEvents(records);
			},
			(error) => {
				console.error("Admin DataContext: Failed to load Twilio events", error);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadEvents]);

	useEffect(() => {
		if (!shouldLoadEvents) {
			setLiveKitEvents([]);
			return;
		}

		console.log("Admin DataContext: Initializing liveKitEvents listener...");
		const unsubscribe = onSnapshot(
			query(
				liveKitEventsCollection(db),
				orderBy("createdAt", "desc"),
				limit(200),
			),
			(snapshot) => {
				console.log(
					`Admin DataContext: Received ${snapshot.docs.length} LiveKit events.`,
				);
				const records = snapshot.docs
					.map((doc) => ({ ...doc.data(), id: doc.id }))
					.filter(Boolean)
					.sort(compareLiveKitEventRecords)
					.map((record) => {
						try {
							return toLiveKitUiEvent(record);
						} catch (err) {
							console.error(`Failed to map LiveKit event ${record.id}`, err);
							return null;
						}
					})
					.filter((record): record is LiveKitEvent => record !== null);

				setLiveKitEvents(records);
			},
			(error) => {
				console.error(
					"Admin DataContext: Failed to load LiveKit events",
					error,
				);
			},
		);
		return () => unsubscribe();
	}, [shouldLoadEvents]);

	useEffect(() => {
		if (!shouldLoadTcdrs) {
			setRecentTcdrs([]);
			setOlderTcdrs([]);
			setTcdrCursor(null);
			setHasMoreTcdrs(false);
			setIsLoadingMoreTcdrs(false);
			return;
		}

		let isActive = true;

		const unsubscribe = onSnapshot(
			query(
				tcdrsCollectionGroup(db),
				orderBy("dateCreated", "desc"),
				limit(TCDR_PAGE_SIZE),
			),
			(snapshot) => {
				const records = snapshot.docs.map((doc) => ({
					...doc.data(),
					id: doc.id,
				}));
				records.sort(compareTcdrRecords);
				setTcdrCursor(
					snapshot.docs.length > 0
						? snapshot.docs[snapshot.docs.length - 1]
						: null,
				);
				setHasMoreTcdrs(snapshot.docs.length === TCDR_PAGE_SIZE);

				void hydrateTcdrRecords(records).then((hydratedRecords) => {
					if (!isActive) {
						return;
					}
					setRecentTcdrs(hydratedRecords);
				});
			},
			(error) => {
				console.error("Failed to load T-CDRs from Firestore", error);
			},
		);

		return () => {
			isActive = false;
			unsubscribe();
		};
	}, [shouldLoadTcdrs]);

	const loadMoreTcdrs = useCallback(async () => {
		if (!shouldLoadTcdrs || !tcdrCursor || isLoadingMoreTcdrs) {
			return;
		}

		setIsLoadingMoreTcdrs(true);
		try {
			const snapshot = await getDocs(
				query(
					tcdrsCollectionGroup(db),
					orderBy("dateCreated", "desc"),
					startAfter(tcdrCursor),
					limit(TCDR_PAGE_SIZE),
				),
			);
			const records = snapshot.docs.map((doc) => ({
				...doc.data(),
				id: doc.id,
			}));
			records.sort(compareTcdrRecords);
			const hydratedRecords = await hydrateTcdrRecords(records);
			setOlderTcdrs((current) => {
				const existingIds = new Set([
					...recentTcdrs.map(tcdrKey),
					...current.map(tcdrKey),
				]);
				return [
					...current,
					...hydratedRecords.filter((tcdr) => !existingIds.has(tcdrKey(tcdr))),
				];
			});
			setTcdrCursor(
				snapshot.docs.length > 0
					? snapshot.docs[snapshot.docs.length - 1]
					: tcdrCursor,
			);
			setHasMoreTcdrs(snapshot.docs.length === TCDR_PAGE_SIZE);
		} catch (error) {
			console.error("Failed to load more T-CDRs from Firestore", error);
		} finally {
			setIsLoadingMoreTcdrs(false);
		}
	}, [isLoadingMoreTcdrs, recentTcdrs, shouldLoadTcdrs, tcdrCursor]);

	const callsWithActionItems = useMemo(() => {
		return calls.map((call) => {
			const callActionItems = actionItems.filter(
				(ai) => ai.callSid === call.id,
			);
			return { ...call, actionItems: callActionItems };
		});
	}, [calls, actionItems]);

	const value = useMemo(
		() => ({
			// Events data
			twilioErrors,
			twilioEvents,
			liveKitEvents,

			// T-CDR data
			tcdrs,
			isLoadingMoreTcdrs,
			hasMoreTcdrs,
			loadMoreTcdrs,

			// Calls data
			calls: callsWithActionItems,

			// Company, User, and Service data
			companies,
			users,
			services,

			// Phone Number and Agent data
			phoneNumbers,
			whatsappSenders,
			agents,
			testRouting,

			// Audit Logs
			auditLogs,

			// Subscriptions & adminActions
			subscriptions,
			adminActions,

			// Aggregated call stats
			dailyCallStats,
		}),
		[
			liveKitEvents,
			tcdrs,
			isLoadingMoreTcdrs,
			hasMoreTcdrs,
			loadMoreTcdrs,
			callsWithActionItems,
			twilioErrors,
			twilioEvents,
			companies,
			users,
			services,
			phoneNumbers,
			whatsappSenders,
			agents,
			testRouting,
			auditLogs,
			subscriptions,
			adminActions,
			dailyCallStats,
		],
	);

	return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => {
	const context = useContext(DataContext);
	if (context === undefined) {
		throw new Error("useData must be used within a DataProvider");
	}
	return context;
};
